import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { FormLabel } from "@impero-com/ui";
import { Control, UseFormTrigger } from "react-hook-form";

import { UserOverview, EntityView } from "api/generated/model";
import { RHFAccessRights } from "components/form/rhfFields/rhfAccessRights";
import { RHFMarkdownInput } from "components/form/rhfFields/rhfMarkdownInput";
import { RHFRadioButtonGroupInput } from "components/form/rhfFields/rhfRadioButtonGroupInput";
import { RHFUserTypeaheadInput } from "components/form/rhfFields/rhfUserTypeaheadInput";
import { RHFEditableControlProgramTagListInput } from "org/control/controlProgram/view/components/rhfEditableControlProgramTagListPlus";
import { LinkedEntitiesTable } from "org/controlEntities/components/LinkedEntitiesTable";
import { ControlProgramTagsQuerySelect } from "org/tags/controlProgramTags/queries/useControlProgramTagsQuery";
import { ControlProgramAccessRightsInstance } from "utils/accessRights/models";
import { useAvailableActions } from "utils/hooks/useAvailableActions";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import * as theme from "./ControlProgramForm.css";
import {
    ControlProgramFormSchema,
    getNameSchema,
} from "./useControlProgramViewFormSchema";

interface ControlProgramFormProps {
    control: Control<ControlProgramFormSchema>;
    trigger: UseFormTrigger<ControlProgramFormSchema>;
    users: UserOverview[];
    canEdit: boolean;
    canEditAccessRights: boolean;
    canEditEntities: boolean;
    initialTags?: ControlProgramTagsQuerySelect;
    linkedEntities: EntityView[];
}

export function ControlProgramForm({
    control,
    trigger,
    users,
    canEdit,
    canEditAccessRights,
    canEditEntities,
    initialTags,
    linkedEntities,
}: ControlProgramFormProps) {
    const { t } = useTranslation();
    const {
        userTag: { create: canCreateTag },
    } = useAvailableActions();
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const isDisabled = !canEdit;

    const programStatusOptions = [
        { label: t("EditControlProgram_Status_Active"), value: true },
        { label: t("EditControlProgram_Status_Inactive"), value: false },
    ];
    return (
        <div>
            <FormSection
                id={"control-program-details"}
                titleLabel={t("EditControlProgram_Details")}
                withDivider
            >
                <RHFStringInput
                    label={t("EditControlProgram_Name")}
                    placeholder={t("EditControlProgram_Name_PlaceHolder")}
                    control={control}
                    name="name"
                    schema={getNameSchema(t)}
                    disabled={isDisabled}
                />
                <RHFMarkdownInput
                    label={t("EditControlProgram_Description")}
                    placeholder={t(
                        "EditControlProgram_Description_PlaceHolder"
                    )}
                    control={control}
                    name="description"
                    disabled={isDisabled}
                />
                <RHFRadioButtonGroupInput
                    control={control}
                    name="running"
                    label={t("EditControlProgram_Status")}
                    options={programStatusOptions}
                    disabled={isDisabled}
                    type="button"
                />

                <RHFEditableControlProgramTagListInput
                    cascaderClassName={theme.tagCascader}
                    control={control}
                    name="tagIds"
                    canCreateTag={canCreateTag}
                    label={t("EditControlProgram_ProgramTagList")}
                    disabled={isDisabled}
                    initialTags={initialTags}
                />
            </FormSection>
            <FormSection
                id={"control-program-access-rights"}
                titleLabel={t("EditControlProgram_Users")}
                withDivider
            >
                <RHFUserTypeaheadInput
                    control={control}
                    name="responsibleId"
                    disabled={!canEditAccessRights}
                    label={t("AccessRights_Responsible")}
                />
                <FormLabel>{t("AccessRights_AccessManagement")}</FormLabel>
                <RHFAccessRights
                    control={control}
                    users={users}
                    name="accessRights"
                    resourceAccessRights={ControlProgramAccessRightsInstance}
                    disabled={!canEditAccessRights}
                />
            </FormSection>
            {controlEntitiesEnabled && (
                <FormSection
                    id={"control-program-entities"}
                    titleLabel={t("EditControlProgram_ControlEntities")}
                >
                    <LinkedEntitiesTable
                        control={control}
                        trigger={trigger}
                        canEditEntities={canEditEntities}
                        linkedEntities={linkedEntities}
                    />
                </FormSection>
            )}
        </div>
    );
}
